/**
 * Plan stewardship — FHIR R5 `CarePlan.contributor[]` and `CarePlan.custodian`.
 *
 * ☠️ THERE IS NO `CarePlan.author` IN FHIR R5. It existed in R4 and was split.
 * Web 1.5.1 briefly stamped `author`; this module replaces it before any plan
 * was written (verified: 198 plans, none carried `author`). R5 says:
 *
 *   contributor[]  0..*  "Who provided the content of the care plan"
 *   custodian      0..1  "the custodian is responsible for the care plan.
 *                         The care plan is attributed to the custodian."
 *
 * That split is exactly the fact/choice distinction this feature needs, and the
 * standard reached it for the same reason: editing a document should not
 * silently transfer responsibility for it. So:
 *
 *   contributor[] = the FACTS — everyone who has saved a version, appended.
 *   custodian     = the CHOICE — the one party answerable, and the person a
 *                   carer's "Notify office" escalation reaches. 0..1 in FHIR,
 *                   which is Ed's "single recipient, never a fan-out" rule
 *                   encoded in the model rather than in our code.
 *
 * DEFAULT (Ed, 2026-09-25): "the latest author of a plan should be the
 * recipient" — so each save makes the saver the custodian. ☠️ Unless the
 * custodian was PINNED: `_custodianPinned` marks a deliberate human choice —
 * the plan builder's "Office contact for escalations" picker (./office-contact)
 * sets it — and a pinned custodian is carried forward untouched. Without that
 * marker, the next colleague to fix a typo would silently inherit
 * responsibility — the very thing R4→R5 split the element to prevent.
 *
 * Absent, never null: with no signed-in `personDocId` we omit `custodian`
 * entirely, the escalation finds none, and it falls back to support@ — exactly
 * the behaviour before this feature existed, which is what lets it ship
 * forward-only with no backfill.
 */

/** The minimal shape of a Firestore DocumentReference this module handles. */
export interface PersonRefLike {
  readonly id: string;
}

/** A FHIR Reference as stored: `{ reference: <native DocumentReference> }`. */
export interface RefEntry<R extends PersonRefLike> {
  reference: R;
}

/** The prior version's stewardship, as read from its `_carePlan` document. */
export interface PriorStewardship {
  contributor?: unknown;
  custodian?: unknown;
  /** House marker: a human chose this custodian, so a save must not move it. */
  _custodianPinned?: unknown;
}

/**
 * What the planner decided about the office contact in THIS editing session.
 *
 *   `undefined` — not asked: carry a prior pin forward (the read-only path,
 *                 and any caller that has no picker).
 *   `null`      — explicitly unpinned: the saver becomes custodian again.
 *   `{ … }`     — pinned to this person.
 *
 * The plan builder always passes `null` or an object, never `undefined`: with
 * a picker on screen, "the planner left it alone" is still a decision about
 * what the UI was showing, and inferring it from the prior document would
 * make clearing the pin impossible.
 */
export type PinnedCustodian = { personDocId: string } | null | undefined;

const isRef = <R extends PersonRefLike>(value: unknown): value is R =>
  typeof value === "object" && value !== null && typeof (value as R).id === "string";

const entryRef = <R extends PersonRefLike>(entry: unknown): R | null => {
  if (typeof entry !== "object" || entry === null) return null;
  const ref = (entry as { reference?: unknown }).reference;
  return isRef<R>(ref) ? ref : null;
};

/**
 * `contributor` + `custodian` for a CarePlan write.
 *
 * `makePersonRef` is injected so this stays pure and testable — plan-builder
 * passes `(id) => doc(db, "_person", id)`. ☠️ It must return a NATIVE
 * DocumentReference, never a path string
 * (feedback_firestore_documentreference_no_string_serialization); prior entries
 * that are not native references are dropped rather than copied forward.
 */
export function planStewardshipFields<R extends PersonRefLike>(
  personDocId: string | null | undefined,
  makePersonRef: (id: string) => R,
  prior?: PriorStewardship | null,
  pinned?: PinnedCustodian,
): { contributor: RefEntry<R>[]; custodian?: RefEntry<R>; _custodianPinned?: true } {
  const carried: RefEntry<R>[] = [];
  const seen = new Set<string>();
  for (const entry of Array.isArray(prior?.contributor) ? prior!.contributor : []) {
    const ref = entryRef<R>(entry);
    if (ref && !seen.has(ref.id)) {
      seen.add(ref.id);
      carried.push({ reference: ref });
    }
  }

  const id = (personDocId ?? "").trim();
  const saver = id ? makePersonRef(id) : null;
  if (saver && !seen.has(saver.id)) carried.push({ reference: saver });

  // This session's explicit choice outranks the prior document: the planner
  // was looking at the picker when they saved. ☠️ A chosen contact is NOT
  // added to `contributor[]` — they have not contributed content, and
  // conflating the two is exactly what R4's single `author` got wrong.
  if (pinned !== undefined) {
    const chosen = (pinned?.personDocId ?? "").trim();
    if (chosen) {
      return {
        contributor: carried,
        custodian: { reference: makePersonRef(chosen) },
        _custodianPinned: true,
      };
    }
    // Explicitly cleared: fall through to the default (the saver), dropping
    // any prior pin — clearing must not be a one-way door.
    return saver ? { contributor: carried, custodian: { reference: saver } } : { contributor: carried };
  }

  // Not asked. A pinned custodian is a human's decision and survives saves.
  const pinnedRef = prior?._custodianPinned === true
    ? entryRef<R>(prior?.custodian)
    : null;
  if (pinnedRef) {
    return { contributor: carried, custodian: { reference: pinnedRef }, _custodianPinned: true };
  }
  if (saver) return { contributor: carried, custodian: { reference: saver } };

  // No saver and no pin: carry a prior custodian rather than dropping
  // responsibility on the floor, else omit the field entirely.
  const priorRef = entryRef<R>(prior?.custodian);
  return priorRef
    ? { contributor: carried, custodian: { reference: priorRef } }
    : { contributor: carried };
}
